import { PayloadAction } from '@reduxjs/toolkit';
import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { MediaStreamKind, Participant, SlotType } from '@yandex-video-platform/goloom-sdk';
import { uniq } from 'lodash';
import { all, call, delay, put, SagaGenerator, select, takeEvery } from 'typed-redux-saga';

import { reactions, SELF } from '../../../../../shared/constants';
import { goloomSdk, isParticipantSharingScreen } from '../../../../../shared/features/goloom-sdk';
import { isModerator, isOwner } from '../../../../../shared/features/moderation/helpers';
import { moderationRoleChanged } from '../../../../../shared/features/moderation/slice';
import {
    hideNotification,
    NOTIFICATIONS_MAP,
    notify,
} from '../../../../../shared/features/notifications/ps-notifications';
import { getShowMyVideo } from '../../../../../shared/features/settings';
import { getUserSelector, updateUser, User } from '../../../../../shared/features/user';
import { LoggableError } from '../../../../../shared/helpers/errors';
import { forIterator } from '../../../../../shared/helpers/for-iterator';
import { isInsideIframe } from '../../../../../shared/helpers/iframe';
import { ACTION, FEATURE } from '../../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { Role } from '../../../../../shared/helpers/permissions';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { GRID_VIEW } from '../../../constants';
import { setOutgoingCallCallee } from '../../../iframe-sagas/slice';
import {
    getActiveGridView,
    getConferenceConfig,
    getConferenceStateVersion,
    getCurrentPage,
    getJoinedAsGuest,
    getMaxDisplayParticipants,
    getMyOldUserIds,
    getMyUserId,
    getParticipantById,
    getParticipantByIds,
    getParticipants,
    selectParticipants,
} from '../../../selectors';
import { NotificationId, ParticipantID, ParticipantsById, ParticipantState } from '../../../types';
import { setCurrentGridPageAction } from '../../goloom-video-grid';
import { getPermissionsVersion } from '../../permissions';
import { updatePermissionsEffect, updatePermissionsInfoSaga } from '../../permissions/update-permissions-saga';
import { setIsLocalRecordingAction } from '../../record/slice';
import { setDesktopSharingStatus } from '../../screen-sharing';
import { CONNECTION_TYPE_TO_PEER_TYPE_MAP } from '../helpers/mappers';
import { isConferenceStatePayload } from '../helpers/notifications-type-guards';
import {
    participantsAddedAction,
    participantsRemovedAction,
    removeParticipant,
    removeParticipantAction,
    removeParticipantById,
    setParticipantById,
    setParticipants,
    setParticipantsByIds,
    updateParticipant,
    updateParticipants,
    userJoinAction,
    userLeftAction,
} from '../slice';
import { RemoveParticipantPayload } from '../types';
import { syncConferenceState } from './conference';

const logger = createLogger('goloom:participants');

const participantsWithSentVideoOnEvent = new Set<string>();

interface UpdatePeersInfoSagaParams {
    force?: boolean;
    selfPeerId?: ParticipantID;
}

export function* updatePeersInfoSaga(
    peersInfo: CloudAPI.PeerInfo[],
    { force, selfPeerId }: UpdatePeersInfoSagaParams = {}
): SagaGenerator<ParticipantsById> {
    const participantsByIds = yield* select(getParticipantByIds);
    const modifiedParticipantsByIds: ParticipantsById = Object.assign({}, participantsByIds);
    for (const peerInfo of peersInfo) {
        const userId = peerInfo.peer_id === (selfPeerId ?? goloomSdk.myUserId) ? SELF : peerInfo.peer_id;
        const participant = participantsByIds[userId];
        const version = participant?.version || -1;

        if ((participant || force) && peerInfo.version > version) {
            const updateData: Partial<ParticipantState> = {
                ...participant,
                isLocalRecording: peerInfo.state.is_local_recording,
                peerType: peerInfo.peer_type,
            };
            const userDataState = peerInfo.state.user_data;
            const { reaction } = peerInfo.state;
            const reactionType = reaction?.reaction_type;

            if (reactionType && reactions.includes(reactionType)) {
                updateData.reaction = {
                    reactionType,
                    timestamp: reaction.timestamp,
                    suppressible: reaction.suppressible,
                };
            } else {
                if (reactionType) {
                    logger.error(
                        new LoggableError('An unknown reactionType was received', {
                            method: 'convertReactionMessage',
                            additional: { reactionType, knownReactions: reactions },
                        })
                    );
                }

                updateData.reaction = { reactionType: null, timestamp: Date.now(), suppressible: false };
            }

            updateData.version = peerInfo.version;

            if (userDataState.avatar_url) {
                updateData.avatarUrl = userDataState.avatar_url;
            }

            if (userDataState.avatar_placeholder) {
                updateData.avatarPlaceholder = {
                    abbreviation: userDataState.avatar_placeholder.abbreviation,
                    backgroundColor: userDataState.avatar_placeholder.background_color,
                    textColor: userDataState.avatar_placeholder.text_color,
                };
            }

            if (userDataState?.uid) {
                updateData.uid = userDataState.uid;
            }

            if (userDataState?.mssngr_guid) {
                updateData.mssngrGuid = userDataState.mssngr_guid;
            }

            if (userDataState.is_default_avatar !== undefined) {
                updateData.isDefaultAvatar = userDataState.is_default_avatar;
            }

            if (userDataState.display_name) {
                updateData.displayName = userDataState.display_name;
            }

            if (userDataState?.role) {
                updateData.isModerator = isModerator(userDataState?.role);
                updateData.isOwner = isOwner(userDataState?.role);
            }

            if (peerInfo.public_permissions_override) {
                updateData.publicPermissionsOverride = {
                    ...(updateData.publicPermissionsOverride ? updateData.publicPermissionsOverride : {}),
                    ...peerInfo.public_permissions_override,
                };
            }

            modifiedParticipantsByIds[userId] = {
                id: userId,
                ...updateData,
            };

            if (userId === SELF) {
                const newUserData: Partial<User> = Object.assign({}, modifiedParticipantsByIds[userId]);

                if (userDataState.avatar_url) {
                    newUserData.avatarUrl = userDataState.avatar_url;
                }

                if (userDataState.display_name) {
                    newUserData.name = userDataState.display_name;
                    newUserData.displayName = userDataState.display_name;
                }

                if (userDataState.avatar_placeholder) {
                    newUserData.avatarPlaceholder = {
                        abbreviation: userDataState.avatar_placeholder.abbreviation,
                        backgroundColor: userDataState.avatar_placeholder.background_color,
                        textColor: userDataState.avatar_placeholder.text_color,
                    };
                }

                // Обновляем имя в goloomSdk, т.к. имя SELF из peer-info может отличаться от имени из users/me
                if (newUserData.name) {
                    yield* call([goloomSdk, goloomSdk.setName], newUserData.name);
                }
                yield* put(updateUser(newUserData));
            }
        }
    }
    yield* put(updateParticipants(modifiedParticipantsByIds));
    return modifiedParticipantsByIds;
}

export function* participantsAddedSaga(participants: Participant[]) {
    const conferenceConfig = yield* select(getConferenceConfig);

    if (!conferenceConfig || !participants.length) {
        return;
    }

    let participantsByIds = yield* select(getParticipantByIds);
    let modifiedParticipantsByIds = Object.assign({}, participantsByIds);
    for (const participant of participants) {
        const userId = participant.userId === goloomSdk.myUserId ? SELF : participant.userId;
        // Мерджим с уже лежащим в сторе, чтобы не затереть cloud-данные (avatarUrl, isModerator
        // и т.д.), которые мог положить updatePeersInfoSaga(config.peers) до SDK-джоина.
        const existing = modifiedParticipantsByIds[userId];
        modifiedParticipantsByIds[userId] = {
            ...existing,
            id: userId,
            displayName: existing?.displayName || participant.name,
            video: { id: '', muted: !participant.sendVideo },
            audio: { id: '', muted: !participant.sendAudio },
            peerType: CONNECTION_TYPE_TO_PEER_TYPE_MAP[participant.connectionType],
            refParticipantId: participant.refParticipant?.userId || null,
        };
    }
    yield* put(updateParticipants(modifiedParticipantsByIds));
    const participantIds = participants.map(({ userId }) => ({ peer_id: userId }));
    let peersInfo: CloudAPI.PeerInfo[] = [];
    let permissionsInfo: Maybe<CloudAPI.PermissionsInfo>;

    const permissionsVersion = yield* select(getPermissionsVersion);
    const requestPermissionsData: CloudAPI.GetPeersState.Request['permissions'] = {
        version: permissionsVersion,
    };

    const conferenceVersion = yield* select(getConferenceStateVersion);
    const requestConferenceData = {
        version: conferenceVersion,
    };

    const payload = {
        peers: participantIds,
        permissions: requestPermissionsData,
        conference: requestConferenceData,
    };

    try {
        const states = yield* call([cloudApi, cloudApi.getPeersState], conferenceConfig.resolved_uri, payload);
        peersInfo = states.peers;
        permissionsInfo = states.permissions;

        if (isConferenceStatePayload(states)) {
            yield* call(syncConferenceState, states.conference);
        }
    } catch (error) {
        logger.error(error, {
            method: 'participantsAddedSaga',
        });
    }

    participantsByIds = yield* call(updatePeersInfoSaga, peersInfo, { force: true });
    const stateParticipants = yield* select(getParticipants);

    modifiedParticipantsByIds = Object.assign({}, participantsByIds);
    for (const participant of participants) {
        const myUserId = yield* select(getMyUserId);
        const isMyUserId = myUserId === participant.userId;

        // В момент, когда мы оказываемся здесь, статус video и audio для SELF может уже измениться
        const { userId, name, sendVideo, sendAudio } = (() => {
            const actualParticipant = (myUserId === participant.userId && goloomSdk.getMe()) || participant;

            return {
                userId: actualParticipant.userId,
                name: actualParticipant.name,
                sendVideo: actualParticipant.sendVideo,
                sendAudio: actualParticipant.sendAudio,
            };
        })();

        // TODO если шарит экран, писать в стор отдельно его id, потом доставать в getScreenSharingParticipant
        const isScreenSharing = isParticipantSharingScreen(participant);
        const participantId = isMyUserId ? SELF : userId;
        const currentParticipantData = modifiedParticipantsByIds[participantId];
        modifiedParticipantsByIds[participantId] = {
            ...currentParticipantData,
            id: participantId,
            // Используем имя из cloud api, если оно есть
            displayName: currentParticipantData?.displayName || name,
            video: {
                id: '',
                muted: !sendVideo,
            },
            audio: {
                id: '',
                muted: !sendAudio,
            },
            desktop: {
                id: '',
                muted: !isScreenSharing,
            },
            refParticipantId: participant.refParticipant?.userId || null,
        };

        const joinedAsGuest = yield* select(getJoinedAsGuest);
        const myOldUserIds = yield* select(getMyOldUserIds);

        // Пропадает плашка со ссылкой при реконнекте
        // https://st.yandex-team.ru/CHEMODAN-72670
        const isShowShareNotification =
            !joinedAsGuest &&
            (stateParticipants.length === 1 || (stateParticipants.length === 2 && myOldUserIds.includes(userId)));

        if (!isShowShareNotification) {
            yield* put(hideNotification(NotificationId.SHARE));
        }

        if (isMyUserId) {
            // Если текущий пользователь есть среди добавленных,
            // а также в peersInfo есть информация, что он ведет запись,
            // то делаем запрос для отключения этого состояния
            if (peersInfo.find((peer) => peer.peer_id === myUserId)?.state.is_local_recording) {
                yield* put(setIsLocalRecordingAction(false));
            }
        }

        if (currentParticipantData && isInsideIframe() && !isMyUserId) {
            yield* put(userJoinAction({ id: currentParticipantData.uid || 'unknown' }));
        }
    }
    yield* put(updateParticipants(modifiedParticipantsByIds));
    yield* put(participantsAddedAction(participants.length));

    if (permissionsInfo) {
        yield* call(updatePermissionsInfoSaga, permissionsInfo);
    }
}

export function* checkCurrentPageSaga(participantsCount: number = goloomSdk.participants.length) {
    const showMyVideo = yield* select(getShowMyVideo);
    const maxDisplayParticipants = yield* select(getMaxDisplayParticipants);
    const currentPage = yield* select(getCurrentPage);
    const activeGridView = yield* select(getActiveGridView);

    const hasSharing = goloomSdk.session?.slots.current.find(
        (slot) => slot.type === SlotType.PARTICIPANT && slot.mediaStreamKind === MediaStreamKind.DISPLAY
    );

    const paginatedParticipantsCount =
        participantsCount + Number(showMyVideo) - Number(activeGridView === GRID_VIEW.PRESENTER && !hasSharing);
    if (currentPage * maxDisplayParticipants >= paginatedParticipantsCount) {
        yield* put(setCurrentGridPageAction(Math.floor(paginatedParticipantsCount / maxDisplayParticipants) - 1));
    }
}

export function* participantsRemovedSaga(participants: Participant[]) {
    for (const { userId } of participants) {
        const participant = yield* select(getParticipantById, userId);
        const { desktop, refParticipantId } = participant || {};

        if (desktop && !desktop.muted) {
            yield* call(setDesktopSharingStatus, 'none');
        }

        participantsWithSentVideoOnEvent.delete(userId);

        const participantsByIds = yield* select(getParticipantByIds);
        const participantChild = Object.values(participantsByIds).find(
            (participant) => participant?.refParticipantId === userId
        );

        yield* put(removeParticipantAction({ id: userId, removeFromMap: !participantChild }));
        const participants = yield* select(selectParticipants);
        if (refParticipantId && !participants.includes(refParticipantId)) {
            yield* put(removeParticipantAction({ id: refParticipantId }));
        }
        yield* put(userLeftAction({ id: participant?.uid || 'unknown' }));
    }
    yield* put(participantsRemovedAction(participants.length));
    yield* call(checkCurrentPageSaga);
}

export function* participantsUpdatedSaga(participants: Participant[]) {
    const participantsByIds = yield* select(getParticipantByIds);

    const modifiedParticipants = Object.assign({}, participantsByIds);

    for (const participant of participants) {
        const userId = participant.userId;
        const currentParticipantData = participantsByIds[userId];

        const currentlyVideoOff = currentParticipantData?.video?.muted;

        if (!currentlyVideoOff && !participant.sendVideo && userId !== SELF) {
            logger.discardFeatureDelta({
                feature: FEATURE.CONFERENCE,
                action: ACTION.PARTICIPANT_VIDEO_ON,
                id: userId,
            });
            participantsWithSentVideoOnEvent.delete(userId);
        }

        if (currentlyVideoOff && participant.sendVideo && userId !== SELF) {
            participantsWithSentVideoOnEvent.delete(userId);
        }

        modifiedParticipants[userId] = {
            ...currentParticipantData,
            id: participant.userId,
            video: {
                id: '',
                muted: !participant.sendVideo,
            },
            audio: {
                id: '',
                muted: !participant.sendAudio,
            },
            displayName: participant.name,
            refParticipantId: participant.refParticipant?.userId || null,
        };
    }
    yield* put(updateParticipants(modifiedParticipants));
}
function createPassportAccountModificationSaga() {
    const chunkSize = 20;
    const batchedPeerList = new Set<string>();
    const peerConferencesMap = new Map<string, string>();
    let isRunning = false;
    const cleanUp = () => {
        batchedPeerList.clear();
        peerConferencesMap.clear();
        isRunning = false;
    };
    return function* passportAccountModificationSaga(peerId: string) {
        const peerConferenceConfig = yield* select(getConferenceConfig);
        if (!peerConferenceConfig) {
            cleanUp();
            return;
        }

        peerConferencesMap.set(peerId, peerConferenceConfig.resolved_uri);
        batchedPeerList.add(peerId);

        if (isRunning) {
            return;
        }
        isRunning = true;
        try {
            while (batchedPeerList.size > 0) {
                try {
                    const conferenceConfig = yield* select(getConferenceConfig);
                    if (!conferenceConfig) {
                        break;
                    }

                    const peersRequest: CloudAPI.GetPeersState.Request['peers'] = [];
                    forIterator(
                        batchedPeerList,
                        (peerId) => {
                            if (peerConferencesMap.get(peerId) === conferenceConfig.resolved_uri) {
                                peersRequest.push({ peer_id: peerId });
                            }
                            batchedPeerList.delete(peerId);
                            peerConferencesMap.delete(peerId);
                        },
                        chunkSize
                    );
                    if (peersRequest.length === 0) {
                        continue;
                    }
                    yield* all([
                        call(function* () {
                            const peersResponse = yield* call(
                                [cloudApi, cloudApi.getPeersState],
                                conferenceConfig.resolved_uri,
                                {
                                    peers: peersRequest,
                                }
                            );
                            yield* call(updatePeersInfoSaga, peersResponse.peers);
                        }),
                        delay(250), // throttle
                    ]);
                } catch (error) {
                    logger.error(error, {
                        method: 'passportAccountModificationSaga',
                    });
                }
            }
        } finally {
            cleanUp();
        }
    };
}
export const passportAccountModificationSaga = createPassportAccountModificationSaga();

const updateParticipantsSaga = function* (action: PayloadAction<ParticipantsById>) {
    yield* put(setParticipantsByIds(action.payload));
    yield* put(setParticipants(Object.keys(action.payload)));
};

function* removeParticipantSaga(action: PayloadAction<RemoveParticipantPayload>) {
    const { id, removeFromMap = true } = action.payload;
    const participant = yield* select(getParticipantById, id);

    if (participant) {
        yield* put(removeParticipant(id));
        if (removeFromMap) {
            yield* put(removeParticipantById(id));
        }
    }
}

function* updateParticipantSaga(participantState: ParticipantState) {
    const myUserId = yield* select(getMyUserId);
    const participant = {
        ...participantState,
        id: myUserId === participantState.id ? SELF : participantState.id,
    };
    const currParticipant = yield* select(getParticipantById, participant.id);
    if (currParticipant) {
        yield* put(
            setParticipantById({
                ...currParticipant,
                ...participant,
            })
        );
    } else {
        const participants = yield* select(selectParticipants);
        yield* put(setParticipantById(participant));
        // На всякий случай обеспечим уникальность, если вдруг participantsByIds и participants разъехались
        const newParticipants = uniq([...participants, participant.id]);
        yield* put(setParticipants(newParticipants));
        if (newParticipants.length >= 2) {
            yield* put(setOutgoingCallCallee(undefined));
        }
    }
}

const onUpdateParticipant = function* (action: PayloadAction<ParticipantState>) {
    yield* call(updateParticipantSaga, action.payload);
};

export function* moderationRoleChangedSaga({ payload: message }: ReturnType<typeof moderationRoleChanged>): Generator {
    const user = yield* select(getUserSelector);
    const participantsByIds = yield* select(getParticipantByIds);
    const myPeerId = yield* select(getMyUserId);
    const isSelfRoleChanged = message.event.peer_id === myPeerId;

    if (message.event.user_role === Role.ADMIN) {
        const iAmAppointedNewModerator = message.source.uid === user?.uid;

        if (isSelfRoleChanged || participantsByIds[message.event.peer_id]) {
            const updateParticipantTask = function* () {
                yield* call(updateParticipantSaga, {
                    id: isSelfRoleChanged ? SELF : message.event.peer_id,
                    isModerator: true,
                });
            };

            // Notifications
            // меня назначили администратором
            if (isSelfRoleChanged) {
                yield* updatePermissionsEffect(updateParticipantTask);
                const moderatorWhoAppointedMe = Object.values(participantsByIds).find(
                    (participant) => participant?.uid === message.source.uid
                );
                yield* put(
                    notify(
                        NOTIFICATIONS_MAP[NotificationId.MODERATION_IAM_NEW_MODERATOR](
                            moderatorWhoAppointedMe?.displayName,
                            message.source.uid
                        )
                    )
                );
            } else {
                yield* call(updateParticipantTask);
            }

            // я назначил администратором
            if (iAmAppointedNewModerator) {
                const newModerator = participantsByIds[message.event.peer_id];
                if (newModerator?.displayName) {
                    yield* put(
                        notify(
                            NOTIFICATIONS_MAP[NotificationId.MODERATION_SOMEBODY_NEW_MODERATOR](
                                newModerator?.displayName,
                                newModerator.uid
                            )
                        )
                    );
                }
            }
        }
    }

    if (message.event.user_role === Role.MEMBER) {
        if (isSelfRoleChanged || participantsByIds[message.event.peer_id]) {
            const updateParticipantTask = function* () {
                yield* call(updateParticipantSaga, {
                    id: isSelfRoleChanged ? SELF : message.event.peer_id,
                    isModerator: false,
                });
            };

            // Notifications
            // я больше не admin
            if (isSelfRoleChanged) {
                yield* call(updatePermissionsEffect, updateParticipantTask);
                yield* put(notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_IAM_NOT_MODERATOR]()));
            } else {
                yield* call(updateParticipantTask);
            }

            // я забрал админку
            if (message.source.uid === user?.uid) {
                const { displayName, uid } = participantsByIds[message.event.peer_id] || {};

                if (displayName) {
                    yield* put(
                        notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_NO_LONGER_MODERATOR](displayName, uid))
                    );
                }
            }
        }
    }
}

export const participantsSagaWatcher = function* () {
    yield* takeEvery(updateParticipants, updateParticipantsSaga);
    yield* takeEvery(updateParticipant, onUpdateParticipant);
    yield* takeEvery(removeParticipantAction, removeParticipantSaga);
    yield* takeEvery(moderationRoleChanged, moderationRoleChangedSaga);
};

export function onParticipantTurnedVideoOn(action: PayloadAction<string>) {
    const participantId = action.payload;

    if (participantsWithSentVideoOnEvent.has(participantId)) {
        return;
    }

    logger.startFeatureDelta({
        feature: FEATURE.CONFERENCE,
        action: ACTION.PARTICIPANT_VIDEO_ON,
        id: participantId,
        timeoutMS: 10_000,
        onFail: () => {
            participantsWithSentVideoOnEvent.add(participantId);
        },
    });
}

export function onParticipantEnabledCameraSuccess(action: PayloadAction<string>) {
    const participantId = action.payload;

    logger.endFeatureDelta({
        feature: FEATURE.CONFERENCE,
        action: ACTION.PARTICIPANT_VIDEO_ON,
        id: participantId,
        isSuccess: true,
    });
    participantsWithSentVideoOnEvent.add(participantId);
}

export function onParticipantEnabledScreenSharingSuccess() {
    logger.endFeatureDelta({
        feature: FEATURE.SHARING,
        action: ACTION.WATCH,
        isSuccess: true,
    });
}

export function onParticipantDisabledCamera(action: PayloadAction<string>) {
    const participantId = action.payload;

    logger.discardFeatureDelta({
        feature: FEATURE.CONFERENCE,
        action: ACTION.PARTICIPANT_VIDEO_ON,
        id: participantId,
    });
}
